import { pickT } from "@repo/shared";
import type {
  FlowField,
  FlowRequirementIssue,
  MoveFlowPageRequest,
  ReorderFlowPagesRequest,
} from "@repo/shared/types";
import { useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfirm } from "@/hooks/use-confirm";
import { useTabParam } from "@/hooks/use-tab-param";
import { ApiError, del, put } from "@/lib/api/_client";
import { checkPageDelete } from "@/lib/api/flow-editor";
import { useAllCustomFields } from "@/lib/hooks/use-clients";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import type { FlowPageWithComponents, PageVisibility } from "@/lib/types/flow";
import { type FlowPageCreateConfig, FlowPageDialog } from "../flow-page-dialog";
import { RequirementIssuesAlert } from "../requirement-issues-alert";
import { T } from "./flow-pages-tab.i18n";
import {
  buildSections,
  type DragContext,
  moveRequestFor,
  type PageSection,
  type SectionKey,
  sectionAvailability,
  sectionLabels,
} from "./page-sections";
import { PageSectionsList } from "./page-sections-list";

const VISIBILITY_TABS: readonly PageVisibility[] = ["internal", "public"];

type FlowPagesTabProps = {
  flowId: string;
  flowLanguage: string;
  pages: FlowPageWithComponents[];
  availableFields: FlowField[];
  /** QL-120 — enables the goal-type source in the page dialog on goal/portfolio pages. */
  portfolioCollectionId?: string | null;
  /** QL-117 — the section labels follow the flow's own repeat flags. */
  multiGoal: boolean;
  multiPortfolio: boolean;
  /** Components the flow's assignments don't support; Activate refuses the flow until they're fixed. */
  requirementIssues?: FlowRequirementIssue[];
  mutate: () => void;
};

/**
 * The flow Pages tab: one bracketed list per visibility (Internal / Public), page create/edit/
 * delete dialogs, and the reorder + move mutations. Layout lives in `PageSectionsList`; the
 * section model in `page-sections.ts`.
 */
export function FlowPagesTab({
  flowId,
  flowLanguage,
  pages,
  availableFields,
  portfolioCollectionId = null,
  multiGoal,
  multiPortfolio,
  requirementIssues,
  mutate,
}: FlowPagesTabProps) {
  const t = pickT(T, flowLanguage);
  // QL-195 — like the parent ?tab= param, the Internal/Public sub-tab lives in
  // the URL (?visibility=) so browser back from a page editor, reloads and the
  // page-editor breadcrumb all land on the sub-tab the user was on. Anything
  // other than "public" falls back to "internal".
  const [visibilityTab, handleVisibilityChange] = useTabParam(VISIBILITY_TABS, "internal", {
    param: "visibility",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<FlowPageWithComponents | null>(null);
  const [deletingPage, setDeletingPage] = useState<FlowPageWithComponents | null>(null);
  const [loading, setLoading] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();

  // Client custom fields for the page dialog's conditional-visibility source
  // picker (the shared component filters on isActive + usable types itself).
  // Standard pseudo-fields are excluded — they are not custom_field rows and
  // their sources exist as dedicated sentinels (Client Country, Client Type, …).
  const { customFields: allCustomFields } = useAllCustomFields();
  const clientCustomFields = useMemo(
    () => allCustomFields.filter((f: { isStandard?: boolean }) => !f.isStandard),
    [allCustomFields],
  );

  // Cross-section drag state (lifted so every section can judge a drop). The ref records that
  // the current drag was dropped into another section, so the source section's `dragend` does
  // not follow the move with a stale same-section reorder.
  const [dragContext, setDragContext] = useState<DragContext>(null);
  const movedAwayRef = useRef(false);

  // Where a new page lands follows from which section's Add Page was pressed.
  const [dialogConfig, setDialogConfig] = useState<FlowPageCreateConfig | null>(null);

  const availability = useMemo(
    () => sectionAvailability(pages, { hasPortfolioCollection: !!portfolioCollectionId }),
    [pages, portfolioCollectionId],
  );
  const labels = useMemo(
    () => sectionLabels(flowLanguage, { multiGoal, multiPortfolio, ...availability }),
    [flowLanguage, multiGoal, multiPortfolio, availability],
  );
  const internalSections = useMemo(() => buildSections(pages, "internal", labels), [pages, labels]);
  const publicSections = useMemo(() => buildSections(pages, "public", labels), [pages, labels]);

  const handleAddToSection = (section: PageSection) => {
    setDialogConfig({
      visibility: section.visibility,
      pageType: section.pageType,
      globalPosition: section.globalPosition,
      goalPosition: section.goalPosition,
    });
    setEditingPage(null);
    setDialogOpen(true);
  };

  const handleEdit = (page: FlowPageWithComponents) => {
    setEditingPage(page);
    setDialogConfig(null);
    setDialogOpen(true);
  };

  const showError = (err: unknown, fallback: string) =>
    confirm({
      title: t.error,
      description: err instanceof ApiError ? err.message : fallback,
      confirmText: t.ok,
      variant: "destructive",
      type: "alert",
    });

  // QL-168 — a page the server would refuse to delete shows why straight away;
  // the confirmation only opens when the delete is allowed.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (pageId) => checkPageDelete(flowId, pageId),
    onAllowed: (pageId) => setDeletingPage(pages.find((p) => p.id === pageId) ?? null),
    onBlocked: (reason) =>
      confirm({
        title: t.error,
        description: reason || t.failedToDeletePage,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      }),
  });
  const handleDelete = (page: FlowPageWithComponents) => requestDelete(page.id);

  const confirmDelete = async () => {
    if (!deletingPage) return;
    setLoading(true);
    try {
      await del(`/internal/flows/${flowId}/pages/${deletingPage.id}`);
      mutate();
      setDeletingPage(null);
    } catch (err) {
      await showError(err, t.failedToDeletePage);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (success: boolean) => {
    setDialogOpen(false);
    setEditingPage(null);
    setDialogConfig(null);
    if (success) mutate();
  };

  // Reorder inside one section: the full ordered id list of that section. On a refusal the
  // flow is refetched (the section may have changed under us) and the error is rethrown so the
  // section can drop its optimistic order.
  const handleReorderWithinSection = async (pageIds: string[]) => {
    setLoading(true);
    try {
      await put(`/internal/flows/${flowId}/pages/reorder`, {
        pageIds,
      } satisfies ReorderFlowPagesRequest);
      mutate();
    } catch (err) {
      mutate();
      // Rethrow first so the section snaps back at once; the dialog stays up on its own.
      void showError(err, t.failedToReorderPages);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Move to another section of the same page type — one request; the server renumbers both
  // sections atomically (QL-117).
  const handleMoveToSection = async (
    pageId: string,
    targetSection: PageSection,
    targetIndex: number,
  ) => {
    movedAwayRef.current = true;
    setLoading(true);
    try {
      await put(
        `/internal/flows/${flowId}/pages/${pageId}/move-section`,
        moveRequestFor(targetSection, targetIndex) satisfies MoveFlowPageRequest,
      );
      mutate();
    } catch (err) {
      mutate();
      await showError(err, t.failedToReorderPages);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (page: FlowPageWithComponents, sectionKey: SectionKey) => {
    movedAwayRef.current = false;
    setDragContext({ page, sourceSection: sectionKey });
  };

  const handleDragEnd = () => {
    setDragContext(null);
  };

  const shared = {
    flowId,
    flowLanguage,
    loading,
    checkingDeleteId: checkingId,
    dragContext,
    onAddToSection: handleAddToSection,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    wasMovedAway: () => movedAwayRef.current,
    onReorderWithinSection: handleReorderWithinSection,
    onMoveToSection: handleMoveToSection,
  };

  return (
    <>
      <div className="space-y-6">
        <p className="text-muted-foreground">{t.pagesDescription}</p>
        <RequirementIssuesAlert issues={requirementIssues} language={flowLanguage} showPages />

        <Tabs value={visibilityTab} onValueChange={handleVisibilityChange}>
          <TabsList>
            <TabsTrigger value="internal">{t.internal}</TabsTrigger>
            <TabsTrigger value="public">{t.public}</TabsTrigger>
          </TabsList>

          <TabsContent value="internal" className="mt-6">
            <PageSectionsList
              sections={internalSections}
              availability={availability}
              hint={t.dragHint}
              {...shared}
            />
          </TabsContent>

          <TabsContent value="public" className="mt-6">
            <PageSectionsList
              sections={publicSections}
              availability={availability}
              hint={t.dragHint}
              {...shared}
            />
          </TabsContent>
        </Tabs>
      </div>

      <FlowPageDialog
        flowId={flowId}
        flowLanguage={flowLanguage}
        page={editingPage}
        availableFields={availableFields}
        clientCustomFields={clientCustomFields}
        portfolioCollectionId={portfolioCollectionId}
        open={dialogOpen}
        onClose={handleDialogClose}
        createConfig={dialogConfig}
      />

      <AlertDialog open={!!deletingPage} onOpenChange={(open) => !open && setDeletingPage(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deletePage}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.deletePageConfirmation(deletingPage?.name || "")}
              {deletingPage && deletingPage.components.length > 0 && (
                <> {t.willAlsoDelete(deletingPage.components.length)}</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>{t.cancel}</AlertDialogCancel>
            <Button variant="destructive" onClick={confirmDelete} loading={loading}>
              {t.delete}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ConfirmationDialog />
    </>
  );
}
